import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useOrg } from "../../lib/org";
import { Card, ErrorState, Money, PageHeader, Spinner } from "../../components/ui";
import { today } from "../../lib/format";
import { DateInput, ReportControls } from "./controls";
import type { AgingReport, AgingRow } from "@shared/types";

const BUCKETS: { key: keyof AgingRow; label: string }[] = [
  { key: "current", label: "Current" },
  { key: "d1_30", label: "1–30" },
  { key: "d31_60", label: "31–60" },
  { key: "d61_90", label: "61–90" },
  { key: "d90_plus", label: "90+" },
];

/** Shared A/R + A/P aging summary. `kind` picks the endpoint and labels. */
export function AgingReportView({ kind }: { kind: "ar" | "ap" }) {
  const { currentOrg } = useOrg();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const [asOf, setAsOf] = useState(today());
  const path = kind === "ar" ? "ar-aging" : "ap-aging";

  const { data, isLoading, error } = useQuery({
    queryKey: [path, currentOrg?.id, asOf],
    queryFn: () => api.get<AgingReport>(`/reporting/aging/${kind}?asOf=${asOf}`),
    enabled: !!currentOrg,
  });

  const title = kind === "ar" ? "A/R Aging Summary" : "A/P Aging Summary";
  const partyHeader = kind === "ar" ? "Customer" : "Vendor";

  return (
    <div>
      <PageHeader title={title} subtitle={`As of ${asOf}`} />
      <p className="mb-4 text-sm text-gray-600">Posted documents and payments through the selected date. Operational invoice mirrors are excluded. Unapplied funds and credits require separate reconciliation to the control account.</p>
      <ReportControls>
        <DateInput label="As of" value={asOf} onChange={setAsOf} />
      </ReportControls>

      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <Card className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="th">{partyHeader}</th>
                {BUCKETS.map((b) => (
                  <th key={b.key} className="th text-right">
                    {b.label}
                  </th>
                ))}
                <th className="th text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.map((r) => (
                <tr key={r.partyId}>
                  <td className="td font-medium">{r.partyName}</td>
                  {BUCKETS.map((b) => (
                    <td key={b.key} className="td text-right">
                      <Money cents={r[b.key] as number} currency={currency} />
                    </td>
                  ))}
                  <td className="td text-right font-semibold">
                    <Money cents={r.total} currency={currency} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300 font-bold">
                <td className="td">Total</td>
                {BUCKETS.map((b) => (
                  <td key={b.key} className="td text-right">
                    <Money cents={data.totals[b.key] as number} currency={currency} />
                  </td>
                ))}
                <td className="td text-right">
                  <Money cents={data.totals.total} currency={currency} />
                </td>
              </tr>
            </tfoot>
          </table>
        </Card>
      )}
    </div>
  );
}

export function ARaging() {
  return <AgingReportView kind="ar" />;
}
export function APaging() {
  return <AgingReportView kind="ap" />;
}
